import Image from 'next/image'
import Link from 'next/link'
import { ComponentPropsWithoutRef, ReactNode, useState } from 'react'
import { Button } from '~shared/components/button-v2'
import { cn } from '~shared/utils/cn'
import { replacePrepositionsWhitespaces } from '~shared/utils/replace-prepositions-whitespaces'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogPaper,
  DialogTrigger
} from '@/modules/components/ui/dialog'
import { TValueOfPromise } from '~shared/api/types'
import { getDiplomasData } from '~shared/api/diplomas/getDiplomasData'

type ImageType = {
  url?: string | null
  width?: number | null
  height?: number | null
}

interface DocumentCardProps extends ComponentPropsWithoutRef<'div'> {
  title: string
  children?: ReactNode
  image: ImageType
}

export const DocumentCard = (props: DocumentCardProps) => {
  const { className = '', title, children, image: rawImage, ...rest } = props
  const image = {
    url: rawImage.url || '',
    width: rawImage.width || 639,
    height: rawImage.height || 900
  }

  const isHorizontal = image.width > image.height

  return (
    <div
      className={cn(
        'p-[16px] md:p-[24px] lg:p-[40px] xl:p-[60px]',
        'rounded-[24px] md:rounded-[40px] xl:rounded-[64px]',
        // 'md:h-[264px] lg:h-[364px] xl:h-[488px]',
        'mt-4 gap-[30px] md:gap-0 bg-secondary grid grid-cols-1 md:grid-cols-2',
        className
      )}
      {...rest}>
      <div className='flex flex-col justify-between md:pr-[16px] lg:pr-[32px] xl:pr-[64px]'>
        <p className='leading-[1] text-[16px] md:text-[20px] xl:text-[32px] font-medium'>
          {replacePrepositionsWhitespaces(title)}
        </p>
        {children ? (
          <p className='mt-[16px] md:mt-0 leading-[1.1] text-[14px] md:text-[16px]'>
            {children}
          </p>
        ) : null}
      </div>

      <Dialog>
        <DialogTrigger asChild>
          <button
            className={cn(
              'flex items-center justify-center w-full',
              isHorizontal ? 'h-full' : 'h-[300px] md:h-full'
            )}>
            <span
              className={cn(
                'relative overflow-hidden rounded-[24px] cursor-pointer',
                isHorizontal
                  ? 'w-full h-auto'
                  : 'h-[210px] md:h-[215px] lg:h-[284px] xl:h-[368px] w-auto self-center md:self-end md:ml-auto'
              )}
              style={{ aspectRatio: `${image.width} / ${image.height}` }}>
              <Image
                src={image.url || ''}
                alt={title}
                fill
                className='object-contain rounded-[24px] hover:scale-110 transition-all duration-300'
              />
            </span>
          </button>
        </DialogTrigger>
        <DialogContent className='box-border max-h-screen w-full px-[10px] md:w-auto md:px-0'>
          <DialogClose />
          <div className={cn(isHorizontal ? 'max-w-[800px]' : '')}>
            <Image
              src={image.url || ''}
              alt={title}
              width={image.width}
              height={image.height}
              className='object-contain rounded-[24px]'
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export interface GraduationDocsSectionProps extends ComponentPropsWithoutRef<'section'> {
  diplomas: TValueOfPromise<typeof getDiplomasData>['value']
  isAll?: boolean
}

export const GraduationDocsSection = (props: GraduationDocsSectionProps) => {
  const { className = '', diplomas, isAll = true, ...rest } = props
  const [tab, setTab] = useState<string>(diplomas?.[0]?.name || '')

  if (!diplomas) return null

  const tabs = [
    ...(isAll
      ? [
          {
            name: 'Аккредитация',
            content: (
              <Link
                target='_blank'
                className='text-[16px] md:text-[20px] leading-[1] underline underline-offset-[3px]'
                href='/assets/docs/Аккредитация МИТМ.pdf'>
                Проверьте государственную аккредитацию образовательной
                деятельности
              </Link>
            ),
            image: {
              width: 639,
              height: 900,
              url: 'https://s3.regru.cloud/mitm-institute/akkreditacziya_MITM_f066a2a1dc.jpg'
            }
          },
          {
            name: 'Лицензия',
            content: (
              <Link
                target='_blank'
                className='text-[16px] md:text-[20px] leading-[1] underline underline-offset-[3px]'
                href='https://islod.obrnadzor.gov.ru/rlic/details/0B100E0A-0A0B-110B-0D10-0E13110C0C111010110E/'>
                Проверьте лицензию на&nbsp;сайте Федеральной службы
                по&nbsp;надзору в&nbsp;сфере образования и&nbsp;науки
              </Link>
            ),
            image: {
              width: 639,
              height: 900,
              url: '/assets/imgs/docs/licenseHigh.jpg'
            }
          }
        ]
      : []),
    ...diplomas.map(
      (diploma: { name: any; imageDefault: any; description: any }) => ({
        name: diploma?.name ?? '',
        image: diploma?.imageDefault ?? '',
        content: diploma?.description ?? ''
      })
    )
  ]

  const currentTab = tabs.find(tabConfig => tabConfig?.name === tab)

  return (
    <section className={cn('container container-slim', className)} {...rest}>
      <h2 className='text-[30px] md:text-[64px] tracking-[-0.04em] font-medium text-center'>
        Документы по&nbsp;окончании обучения
      </h2>
      {tabs?.length > 1 ? (
        <div
          className={cn(
            'flex lg:grid md:grid-cols-3 lg:grid-cols-4 lg:gap-[16px] whitespace-nowrap overflow-x-auto',
            'mt-[30px] md:mt-[24px] lg:mt-[40px] xl:mt-[60px]'
          )}>
          {tabs?.map(diploma => (
            <div
              key={diploma.name}
              className='flex-shrink-0 lg:flex-initial md:basis-1/3 lg:basis-1/4 pr-[8px] lg:pr-0 overflow-hidden rounded-[16px] md:rounded-[24px]'>
              <Button
                size='none'
                variant='none'
                wrap
                className={cn(
                  'min-h-[50px] md:min-h-[67px] px-[24px] max-w-full w-full !rounded-[16px] md:!rounded-[24px]',
                  tab === diploma?.name
                    ? 'bg-accent text-primary'
                    : 'bg-secondary text-tertiary'
                )}
                onClick={() => setTab(diploma.name!)}>
                {diploma.name}
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      <DocumentCard
        key={currentTab?.name ?? ''}
        title={currentTab?.name ?? ''}
        image={currentTab?.image ?? ''}>
        {currentTab?.content ?? ''}
      </DocumentCard>
    </section>
  )
}
